import { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import DashboardTopbar from "components/DashboardTopbar";
import ExpandIcon from "components/ExpandIcon";
import DashboardMenuBar from "components/DashboardMenuBar";
import AllocBarCanvas from "components/AllocBarCanvas";
import ConcChart from "components/ConcChart";
import { useAppContext } from "components/Context/AppContext";
import { fmtDec } from "utils/format";
import "styles/dashboard.css";
import "./risk.css";

const DASHBOARD_API =
  process.env.REACT_APP_API_URL || "http://localhost:5050/api";

// ── Colour helpers ─────────────────────────────────────────────────────────────
function hexToRgb(h) {
  const n = parseInt(h.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
function lerpColour(a, b, t) {
  const ca = hexToRgb(a), cb = hexToRgb(b);
  return `rgb(${Math.round(ca[0] + (cb[0] - ca[0]) * t)},${Math.round(ca[1] + (cb[1] - ca[1]) * t)},${Math.round(ca[2] + (cb[2] - ca[2]) * t)})`;
}

// Format a date value as MM/DD/YYYY (zero-padded); returns the original if unparseable
function fmtDate(v) {
  if (v == null) return v;
  const s = String(v).trim();
  let m, d, y, match;
  if ((match = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/)))        { y = +match[1]; m = +match[2]; d = +match[3]; }
  else if ((match = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})/))) { m = +match[1]; d = +match[2]; y = +match[3]; }
  else {
    const dt = new Date(s);
    if (isNaN(dt.getTime())) return v;
    m = dt.getMonth() + 1; d = dt.getDate(); y = dt.getFullYear();
  }
  if (y < 100) y += 2000;
  return `${String(m).padStart(2, "0")}/${String(d).padStart(2, "0")}/${y}`;
}

// ── SVG Gauge ──────────────────────────────────────────────────────────────────
function SvgGauge({ min = 0, max, value, target, targetLabel, colourFn, reading, readingColor }) {
  const cx = 140, cy = 138, r = 105, SEGS = 80;
  const safeVal = Number.isFinite(value) ? value : min;
  const safeMax = (Number.isFinite(max) && max > min) ? max : Math.max(safeVal * 1.5, min + 1);
  const toDeg = (v) => 180 - ((v - min) / (safeMax - min)) * 180;
  const toXY  = (deg, radius) => ({
    x: cx + radius * Math.cos((deg * Math.PI) / 180),
    y: cy - radius * Math.sin((deg * Math.PI) / 180),
  });

  const segments = [];
  for (let i = 0; i < SEGS; i++) {
    const v0 = min + (i / SEGS) * (safeMax - min);
    const v1 = min + ((i + 1) / SEGS) * (safeMax - min);
    const p0 = toXY(toDeg(v0), r), p1 = toXY(toDeg(v1), r);
    segments.push(
      <path key={i}
        d={`M ${p0.x.toFixed(2)} ${p0.y.toFixed(2)} A ${r} ${r} 0 0 1 ${p1.x.toFixed(2)} ${p1.y.toFixed(2)}`}
        fill="none" stroke={colourFn((v0 + v1) / 2)} strokeWidth="14" strokeLinecap="butt"
      />
    );
  }

  const clampedValue = Math.max(min, Math.min(safeMax, safeVal));
  const tip    = toXY(toDeg(clampedValue), r - 5);
  const ta     = toDeg(target);
  const tOuter = toXY(ta, r + 11), tInner = toXY(ta, r - 11);
  const lp     = toXY(ta, r + 24);

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
      <svg viewBox="0 0 280 148" width="100%"
        style={{ display: "block", overflow: "visible", maxWidth: 160 }}>
        <defs>
          <filter id="rsk-nd">
            <feDropShadow dx="0" dy="1" stdDeviation="2" floodColor="rgba(0,0,0,0.5)" />
          </filter>
        </defs>
        {segments}
        <line
          x1={cx} y1={cy} x2={tip.x.toFixed(2)} y2={tip.y.toFixed(2)}
          stroke="rgba(255,255,255,0.9)" strokeWidth="2" strokeLinecap="round"
          filter="url(#rsk-nd)"
        />
        <circle cx={cx} cy={cy} r="5.5" fill="var(--dash-bg)" stroke="rgba(255,255,255,0.55)" strokeWidth="1.5" />
        <circle cx={cx} cy={cy} r="2" fill="rgba(255,255,255,0.7)" />
        <line
          x1={tOuter.x.toFixed(2)} y1={tOuter.y.toFixed(2)}
          x2={tInner.x.toFixed(2)} y2={tInner.y.toFixed(2)}
          stroke="rgba(255,255,255,0.6)" strokeWidth="1.5" strokeLinecap="round"
        />
        <text
          x={lp.x.toFixed(1)} y={lp.y.toFixed(1)}
          fontSize="16" fill="rgba(255,255,255,0.85)"
          textAnchor="middle" fontFamily="'DM Mono', monospace"
        >
          {targetLabel ?? target}
        </text>
      </svg>
      <div style={{
        fontFamily: "var(--dash-font-d)", fontSize: "1.625rem",
        color: readingColor, textAlign: "center", lineHeight: 1, marginTop: -2,
      }}>
        {reading}
      </div>
    </div>
  );
}

// ── Contributor row ────────────────────────────────────────────────────────────
function ContribRow({ name, pct, maxAbs }) {
  const isHedge  = pct < 0;
  const barWidth = `${((Math.abs(pct) / maxAbs) * 100).toFixed(1)}%`;
  const barColor = isHedge ? "var(--dash-gold-text)" : "var(--dash-blue)";
  return (
    <div className="risk-contrib-row">
      <div className="risk-contrib-name">{name}</div>
      <div className="risk-contrib-bar-track">
        <div className="risk-contrib-bar-fill" style={{ width: barWidth, background: barColor, opacity: 0.85 }} />
      </div>
      <div className="risk-contrib-val" style={{ color: isHedge ? "var(--dash-gold-text)" : "var(--dash-text)" }}>
        {pct > 0 ? "+" : ""}{fmtDec(pct)}%
      </div>
    </div>
  );
}

// ── Alloc chart card (card chrome + expand button + canvas) ───────────────────
function AllocCard({ title, levels, theme, onExpand, labelWidth }) {
  const [currentLevel, setCurrentLevel] = useState("all");
  if (!levels) {
    return (
      <div className="trg-card">
        <div className="trg-card-header"><div className="trg-card-title">{title}</div></div>
        <div className="risk-alloc-body trg-no-data">No data</div>
      </div>
    );
  }
  return (
    <div className="trg-card">
      <div className="trg-card-header">
        <div className="trg-card-title">{title}</div>
        <button className="trg-expand-btn" onClick={() => onExpand(title, currentLevel, levels, labelWidth)}><ExpandIcon /></button>
      </div>
      <div className="risk-alloc-body">
        <AllocBarCanvas
          levels={levels}
          theme={theme}
          initialLevel="all"
          onLevelChange={setCurrentLevel}
          showVar
          labelWidth={labelWidth}
          gap={12}
          padTop={4}
          axisH={22}
        />
      </div>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────
export default function RiskPage() {
  const { selectedAccount } = useAppContext();
  const [theme, setTheme]   = useState(() => localStorage.getItem("trg_theme") || "dark");
  const [fsPanel, setFsPanel] = useState(null);

  const [parameters,    setParameters]    = useState(null);
  const [summary,       setSummary]       = useState(null);
  const [concentrations,setConcentrations]= useState(null);
  const [contributions, setContributions] = useState(null);
  const [assetLevels,   setAssetLevels]   = useState(null);
  const [regionLevels,  setRegionLevels]  = useState(null);
  const [industryLevels,setIndustryLevels]= useState(null);
  const [currencyLevels,setCurrencyLevels]= useState(null);
  const [loading,       setLoading]       = useState(false);

  useEffect(() => {
    const token = sessionStorage.getItem("token");
    const aid   = selectedAccount?.account_id;
    if (!token || !aid) return;

    const q = `?token=${token}&account_id=${aid}`;
    setLoading(true);

    Promise.all([
      fetch(`${DASHBOARD_API}/risk/parameters${q}`).then(r => r.json()),
      fetch(`${DASHBOARD_API}/risk/summary${q}`).then(r => r.json()),
      fetch(`${DASHBOARD_API}/risk/concentrations${q}`).then(r => r.json()),
      fetch(`${DASHBOARD_API}/risk/contributions${q}`).then(r => r.json()),
      fetch(`${DASHBOARD_API}/risk/asset${q}`).then(r => r.json()),
      fetch(`${DASHBOARD_API}/risk/region${q}`).then(r => r.json()),
      fetch(`${DASHBOARD_API}/risk/industry${q}`).then(r => r.json()),
      fetch(`${DASHBOARD_API}/risk/currency${q}`).then(r => r.json()),
    ])
      .then(([params, summ, conc, contrib, asset, region, industry, currency]) => {
        setParameters(Array.isArray(params) ? params : null);
        setSummary(summ && summ.measures ? summ : null);
        setConcentrations(Array.isArray(conc) && conc.length ? conc : null);
        setContributions(Array.isArray(contrib) && contrib.length ? contrib : null);
        setAssetLevels(asset && !asset.error ? asset : null);
        setRegionLevels(region && !region.error ? region : null);
        setIndustryLevels(industry && !industry.error ? industry : null);
        setCurrencyLevels(currency && !currency.error ? currency : null);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [selectedAccount]);

  // ── Gauge data ──
  const gaugeRisk       = summary?.gaugeRisk;
  const gaugeSharpeVar  = summary?.gaugeSharpeVar;
  const gaugeVol        = summary?.gaugeVol;
  const gaugeSharpeVol  = summary?.gaugeSharpeVol;
  const gaugeES         = summary?.gaugeES;
  const gaugeSharpeES   = summary?.gaugeSharpeES;
  const gaugeBeta       = summary?.gaugeBeta;

  // VaR vs Limit
  const riskMax    = gaugeRisk ? Math.max(gaugeRisk.value, gaugeRisk.limit) * 1.15 : 30;
  const riskColour = (v) => {
    if (!gaugeRisk) return "#C9A84C";
    const { limit, band } = gaugeRisk;
    const p = v / riskMax;
    const pY = (limit - band) / riskMax, pR = (limit + band) / riskMax;
    if (p <= pY) return lerpColour("#22d47e", "#C9A84C", Math.min(1, p / pY));
    if (p <= pR) return lerpColour("#C9A84C", "#e05252", (p - pY) / (pR - pY));
    return "#e05252";
  };
  const varDiff      = gaugeRisk ? gaugeRisk.value - gaugeRisk.limit : 0;
  const varReadColor = gaugeRisk
    ? (varDiff < -gaugeRisk.band ? "#22d47e" : varDiff < gaugeRisk.band ? "#C9A84C" : "#e05252")
    : "#C9A84C";

  // SR (VaR) vs BMK
  const sharpeVarColour = (v) => {
    if (!gaugeSharpeVar) return "#C9A84C";
    const { target, band, max } = gaugeSharpeVar;
    const p = v / max;
    const lo = (target - band) / max, hi = (target + band) / max;
    if (p <= lo) return "#e05252";
    if (p <= hi) return lerpColour("#e05252", "#C9A84C", (p - lo) / (hi - lo));
    return lerpColour("#C9A84C", "#22d47e", Math.min(1, (p - hi) / (1 - hi)));
  };
  const sharpeVarDiff      = gaugeSharpeVar ? gaugeSharpeVar.value - gaugeSharpeVar.target : 0;
  const sharpeVarReadColor = gaugeSharpeVar
    ? (sharpeVarDiff > gaugeSharpeVar.band ? "#22d47e" : sharpeVarDiff > -gaugeSharpeVar.band ? "#C9A84C" : "#e05252")
    : "#C9A84C";

  // Vol vs BMK (low = good, like risk gauge)
  const volMax    = gaugeVol ? gaugeVol.max : 12.75;
  const volColour = (v) => {
    if (!gaugeVol) return "#C9A84C";
    const { bmk, band } = gaugeVol;
    const p = v / volMax;
    const pY = (bmk - band) / volMax, pR = (bmk + band) / volMax;
    if (p <= pY) return lerpColour("#22d47e", "#C9A84C", Math.min(1, p / pY));
    if (p <= pR) return lerpColour("#C9A84C", "#e05252", (p - pY) / (pR - pY));
    return "#e05252";
  };
  const volDiff      = gaugeVol ? gaugeVol.value - gaugeVol.bmk : 0;
  const volReadColor = gaugeVol
    ? (volDiff < -gaugeVol.band ? "#22d47e" : volDiff < gaugeVol.band ? "#C9A84C" : "#e05252")
    : "#C9A84C";

  // SR (Vol) vs BMK
  const sharpeVolColour = (v) => {
    if (!gaugeSharpeVol) return "#C9A84C";
    const { target, band, max } = gaugeSharpeVol;
    const p = v / max;
    const lo = (target - band) / max, hi = (target + band) / max;
    if (p <= lo) return "#e05252";
    if (p <= hi) return lerpColour("#e05252", "#C9A84C", (p - lo) / (hi - lo));
    return lerpColour("#C9A84C", "#22d47e", Math.min(1, (p - hi) / (1 - hi)));
  };
  const sharpeVolDiff      = gaugeSharpeVol ? gaugeSharpeVol.value - gaugeSharpeVol.target : 0;
  const sharpeVolReadColor = gaugeSharpeVol
    ? (sharpeVolDiff > gaugeSharpeVol.band ? "#22d47e" : sharpeVolDiff > -gaugeSharpeVol.band ? "#C9A84C" : "#e05252")
    : "#C9A84C";

  // ES 95% vs Limit (low = good, like VaR)
  const esMax = gaugeES ? Math.max(gaugeES.value, gaugeES.limit) * 1.15 : 37_375_000;
  const esColour = (v) => {
    if (!gaugeES) return "#C9A84C";
    const { limit, band } = gaugeES;
    const p = v / esMax;
    const pY = (limit - band) / esMax, pR = (limit + band) / esMax;
    if (p <= pY) return lerpColour("#22d47e", "#C9A84C", Math.min(1, p / pY));
    if (p <= pR) return lerpColour("#C9A84C", "#e05252", (p - pY) / (pR - pY));
    return "#e05252";
  };
  const esDiff      = gaugeES ? gaugeES.value - gaugeES.limit : 0;
  const esReadColor = gaugeES
    ? (esDiff < -gaugeES.band ? "#22d47e" : esDiff < gaugeES.band ? "#C9A84C" : "#e05252")
    : "#C9A84C";

  // SR (ES) vs BMK (high = good, like SR-VaR)
  const sharpeEsColour = (v) => {
    if (!gaugeSharpeES) return "#C9A84C";
    const { target, band, max } = gaugeSharpeES;
    const p = v / max;
    const lo = (target - band) / max, hi = (target + band) / max;
    if (p <= lo) return "#e05252";
    if (p <= hi) return lerpColour("#e05252", "#C9A84C", (p - lo) / (hi - lo));
    return lerpColour("#C9A84C", "#22d47e", Math.min(1, (p - hi) / (1 - hi)));
  };
  const sharpeEsDiff      = gaugeSharpeES ? gaugeSharpeES.value - gaugeSharpeES.target : 0;
  const sharpeEsReadColor = gaugeSharpeES
    ? (sharpeEsDiff > gaugeSharpeES.band ? "#22d47e" : sharpeEsDiff > -gaugeSharpeES.band ? "#C9A84C" : "#e05252")
    : "#C9A84C";

  // Beta vs S&P (below 1.0 = good, like vol)
  const betaMax = gaugeBeta ? gaugeBeta.max : 2.0;
  const betaColour = (v) => {
    if (!gaugeBeta) return "#C9A84C";
    const { bmk, band } = gaugeBeta;
    const p = v / betaMax;
    const pY = (bmk - band) / betaMax, pR = (bmk + band) / betaMax;
    if (p <= pY) return lerpColour("#22d47e", "#C9A84C", Math.min(1, p / pY));
    if (p <= pR) return lerpColour("#C9A84C", "#e05252", (p - pY) / (pR - pY));
    return "#e05252";
  };
  const betaDiff      = gaugeBeta ? gaugeBeta.value - gaugeBeta.bmk : 0;
  const betaReadColor = gaugeBeta
    ? (betaDiff < -gaugeBeta.band ? "#22d47e" : betaDiff < gaugeBeta.band ? "#C9A84C" : "#e05252")
    : "#C9A84C";

  // ── Derived max values ──
  const contribMax = contributions  ? Math.max(...contributions.map(d => Math.abs(d.pct))) : 1;

  // ── Fullscreen helpers ──
  const openAllocFs = (title, initialLevel, levels, labelWidth) => setFsPanel({
    title,
    content: (
      <AllocBarCanvas levels={levels} theme={theme} initialLevel={initialLevel} showVar labelWidth={labelWidth} gap={12} padTop={4} axisH={22} />
    ),
  });

  const openConcFs = () => {
    if (!concentrations) return;
    setFsPanel({
      title: "Concentrations vs Limits",
      content: <ConcChart rows={concentrations} />,
    });
  };

  const openContribFs = () => {
    if (!contributions) return;
    setFsPanel({
      title: "Top Risk Contributors / Hedges",
      content: (
        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", padding: "0.5rem 0" }}>
          {contributions.map(d => (
            <ContribRow key={d.name} name={d.name} pct={d.pct} maxAbs={contribMax} />
          ))}
        </div>
      ),
    });
  };

  const closeFs = () => setFsPanel(null);

  const fundName = selectedAccount?.account_name || selectedAccount?.short_name || "Company Name";
  const measures = summary?.measures ?? [];

  return (
    <>
      <Helmet>
        <title>Risk – Tail Risk Global</title>
      </Helmet>

      <div className={`trg-shell risk-page${theme === "light" ? " theme-light" : ""}`}>
        <DashboardTopbar />
        <DashboardMenuBar onThemeChange={setTheme} />

        <main className="risk-body">

          {loading && <div className="trg-loading">Loading…</div>}

          {/* ── Row 1: Portfolio Settings · Risk Measures · Gauges ── */}
          <div className="risk-row1">

            {/* Portfolio Settings */}
            <div className="trg-card">
              <div className="trg-card-header">
                <div className="trg-card-title">Portfolio Settings</div>
              </div>
              <div className="risk-settings-body">
                {[
                  { label: "Size",          aliases: ["Size", "Market Value", "AUM"] },
                  { label: "As of Date",    aliases: ["As of Date", "As-of Date"] },
                  { label: "Frequency",     aliases: ["Frequency"] },
                  { label: "Risk Measure",  aliases: ["Risk Measure", "Tail Measure"] },
                  { label: "Base Currency", aliases: ["Base Currency", "Currency"] },
                  { label: "Benchmark",     aliases: ["Benchmark"] },
                  { label: "Exp. Returns",  aliases: ["Exp. Returns", "Expected Returns"] },
                ].map(({ label, aliases }) => {
                  const p = (parameters || []).find((x) =>
                    aliases.some((a) => a.toLowerCase() === String(x.label).toLowerCase())
                  );
                  const value = p ? (/date/i.test(label) ? fmtDate(p.value) : p.value) : "—";
                  return (
                    <div key={label} className="risk-settings-row">
                      <span className="risk-settings-label">{label}</span>
                      {p && p.badge
                        ? <span className="risk-meta-badge">{value}</span>
                        : <span className="risk-settings-val">{value}</span>
                      }
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Gauges */}
            <div className="trg-card">
              <div className="trg-card-header">
                <div className="trg-card-title">Gauges</div>
              </div>
              {gaugeRisk && gaugeSharpeVar && gaugeES && gaugeSharpeES && gaugeVol && gaugeSharpeVol && gaugeBeta ? (
                <>
                  <div className="risk-gauges-row">
                    <div className="risk-gauge-section">
                      <div className="risk-section-label">VAR 95% vs Limit</div>
                      <SvgGauge
                        min={0}
                        max={riskMax}
                        value={gaugeRisk.value}
                        target={gaugeRisk.limit}
                        targetLabel={gaugeRisk.targetLabel ?? `${gaugeRisk.limit.toFixed(0)}M`}
                        colourFn={riskColour}
                        reading={
                          <>{gaugeRisk.readingValue ?? gaugeRisk.value.toFixed(1)}<span style={{ fontFamily: "var(--dash-font-b)", fontSize: "0.938rem", color: "var(--dash-muted)", marginLeft: 2 }}>{gaugeRisk.readingUnit ?? "M"}</span></>
                        }
                        readingColor={varReadColor}
                      />
                    </div>
                    <div className="risk-gauge-section">
                      <div className="risk-section-label">SR (VaR) vs BMK</div>
                      <SvgGauge
                        min={0}
                        max={gaugeSharpeVar.max}
                        value={gaugeSharpeVar.value}
                        target={gaugeSharpeVar.target}
                        targetLabel={fmtDec(gaugeSharpeVar.target)}
                        colourFn={sharpeVarColour}
                        reading={fmtDec(gaugeSharpeVar.value)}
                        readingColor={sharpeVarReadColor}
                      />
                    </div>
                    <div className="risk-gauge-section">
                      <div className="risk-section-label">ES 95% vs Limit</div>
                      <SvgGauge
                        min={0}
                        max={esMax}
                        value={gaugeES.value}
                        target={gaugeES.limit}
                        targetLabel={gaugeES.targetLabel ?? `${gaugeES.limit.toFixed(0)}M`}
                        colourFn={esColour}
                        reading={
                          <>{gaugeES.readingValue ?? gaugeES.value.toFixed(1)}<span style={{ fontFamily: "var(--dash-font-b)", fontSize: "0.938rem", color: "var(--dash-muted)", marginLeft: 2 }}>{gaugeES.readingUnit ?? "M"}</span></>
                        }
                        readingColor={esReadColor}
                      />
                    </div>
                    <div className="risk-gauge-section">
                      <div className="risk-section-label">SR (ES) vs BMK</div>
                      <SvgGauge
                        min={0}
                        max={gaugeSharpeES.max}
                        value={gaugeSharpeES.value}
                        target={gaugeSharpeES.target}
                        targetLabel={fmtDec(gaugeSharpeES.target)}
                        colourFn={sharpeEsColour}
                        reading={fmtDec(gaugeSharpeES.value)}
                        readingColor={sharpeEsReadColor}
                      />
                    </div>
                    <div className="risk-gauge-section">
                      <div className="risk-section-label">Vol vs BMK</div>
                      <SvgGauge
                        min={0}
                        max={volMax}
                        value={gaugeVol.value}
                        target={gaugeVol.bmk}
                        targetLabel={fmtDec(gaugeVol.bmk) + "%"}
                        colourFn={volColour}
                        reading={<>{fmtDec(gaugeVol.value)}<span style={{ fontFamily: "var(--dash-font-b)", fontSize: "0.938rem", color: "var(--dash-muted)", marginLeft: 2 }}>%</span></>}
                        readingColor={volReadColor}
                      />
                    </div>
                    <div className="risk-gauge-section">
                      <div className="risk-section-label">SR (Vol) vs BMK</div>
                      <SvgGauge
                        min={0}
                        max={gaugeSharpeVol.max}
                        value={gaugeSharpeVol.value}
                        target={gaugeSharpeVol.target}
                        targetLabel={fmtDec(gaugeSharpeVol.target)}
                        colourFn={sharpeVolColour}
                        reading={fmtDec(gaugeSharpeVol.value)}
                        readingColor={sharpeVolReadColor}
                      />
                    </div>
                    <div className="risk-gauge-section">
                      <div className="risk-section-label">Beta vs S&amp;P</div>
                      <SvgGauge
                        min={0}
                        max={betaMax}
                        value={gaugeBeta.value}
                        target={gaugeBeta.bmk}
                        targetLabel={fmtDec(gaugeBeta.bmk)}
                        colourFn={betaColour}
                        reading={fmtDec(gaugeBeta.value)}
                        readingColor={betaReadColor}
                      />
                    </div>
                  </div>
                  <div className="risk-gauge-note">VaR-based gauges use VaR 95% 1D · Volatility gauges are annualised</div>
                </>
              ) : (
                !loading && <div className="trg-no-data">No data</div>
              )}
            </div>

          </div>{/* /row1 */}

          {/* ── Row 2: Concentrations · Contributors ── */}
          <div className="risk-row2">

            <div className="trg-card">
              <div className="trg-card-header">
                <div className="trg-card-title">Concentrations vs Limits</div>
                {concentrations && <button className="trg-expand-btn" onClick={openConcFs}><ExpandIcon /></button>}
              </div>
              <div className="risk-conc-body">
                {concentrations
                  ? <ConcChart rows={concentrations} />
                  : !loading && <div className="trg-no-data">No data</div>
                }
              </div>
            </div>

            <div className="trg-card">
              <div className="trg-card-header">
                <div className="trg-card-title">Top Risk Contributors / Hedges</div>
                {contributions && <button className="trg-expand-btn" onClick={openContribFs}><ExpandIcon /></button>}
              </div>
              <div className="risk-contrib-body">
                {contributions
                  ? contributions.map(d => (
                      <ContribRow key={d.name} name={d.name} pct={d.pct} maxAbs={contribMax} />
                    ))
                  : !loading && <div className="trg-no-data">No data</div>
                }
              </div>
            </div>

          </div>{/* /row2 */}

          {/* ── Allocation 2×2 ── */}
          <div className="risk-alloc-grid">
            <AllocCard
              title="Asset Allocation"
              levels={assetLevels}
              theme={theme}
              onExpand={openAllocFs}
            />
            <AllocCard
              title="Region Allocation"
              levels={regionLevels}
              theme={theme}
              onExpand={openAllocFs}
            />
            <AllocCard
              title="Industry Allocation"
              levels={industryLevels}
              theme={theme}
              onExpand={openAllocFs}
              labelWidth={140}
            />
            <AllocCard
              title="Currency Allocation"
              levels={currencyLevels}
              theme={theme}
              onExpand={openAllocFs}
            />
          </div>

        </main>

        {/* ── Fullscreen overlay ── */}
        {fsPanel && (
          <div
            className="trg-fs-overlay active"
            onClick={(e) => { if (e.target === e.currentTarget) closeFs(); }}
          >
            <div className="trg-fs-panel">
              <div className="trg-fs-header">
                <div className="trg-fs-title">{fsPanel.title}</div>
                <button className="trg-fs-close" onClick={closeFs}>✕ close</button>
              </div>
              <div className="trg-fs-body">
                {fsPanel.content}
              </div>
            </div>
          </div>
        )}

      </div>
    </>
  );
}
